import { motion } from 'framer-motion';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export const EXPANDED_TABLE_ROWS_TRANSITION = {
  duration: 0.28,
  ease: 'easeInOut',
} as const;

const CONTENT_FADE_TRANSITION = {
  duration: 0.2,
  ease: 'easeInOut',
} as const;

type ExpandedTableRowsPanelProps = {
  children: ReactNode;
  // Span the full row regardless of column count.
  colSpan?: number;
  // When provided, the content fades on key change (e.g. 'loading' -> 'loaded') so the
  // loaded rows do not pop in over the skeleton.
  contentKey?: string;
  isExpanded: boolean;
  // Fires true when expansion starts and false once the collapse animation finishes —
  // lets the parent keep the expanded rows mounted until the collapse is visually done.
  onExpandedVisualChange?: (isExpandedVisual: boolean) => void;
  // Real <table> layout needs column widths synced from the header so the nested rows
  // stay aligned. Flex-styled tables (rows are `display:flex`) don't — pass false there.
  syncColumns?: boolean;
};

export const ExpandedTableRowsPanel: FC<ExpandedTableRowsPanelProps> = ({
  children,
  colSpan = 999,
  contentKey,
  isExpanded,
  onExpandedVisualChange,
  syncColumns = true,
}) => {
  const cellRef = useRef<HTMLTableCellElement>(null);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  // While animating we must clip (height collapse); once open, allow overflow so row
  // action menus / tooltips / popovers are not cut off.
  const [allowOverflow, setAllowOverflow] = useState(false);

  useEffect(() => {
    if (!isExpanded) {
      setAllowOverflow(false);
    }
  }, [isExpanded]);

  const measureColumnWidths = useCallback(() => {
    if (!syncColumns) return;

    // Walk up to the nearest ancestor table that actually has a header row. When this
    // panel is nested inside another panel's column-synced table (deep expand levels),
    // that inner table has no <thead>, so we skip it and align to the real outer header.
    let node: Element | null | undefined = cellRef.current;
    let headerRow: Element | null = null;
    while (node) {
      const ancestorTable: HTMLTableElement | null = node.closest('table');
      if (!ancestorTable) break;
      headerRow = ancestorTable.querySelector(':scope > thead > tr');
      if (headerRow) break;
      node = ancestorTable.parentElement;
    }
    if (!headerRow) return;

    setColumnWidths(Array.from(headerRow.children, (cell) => cell.getBoundingClientRect().width));
  }, [syncColumns]);

  useLayoutEffect(() => {
    if (!isExpanded || !syncColumns) return;

    measureColumnWidths();

    const table = cellRef.current?.closest('table');
    if (!table) return;

    const resizeObserver = new ResizeObserver(measureColumnWidths);
    resizeObserver.observe(table);

    return () => resizeObserver.disconnect();
  }, [isExpanded, measureColumnWidths, syncColumns, children]);

  useEffect(() => {
    if (isExpanded) {
      onExpandedVisualChange?.(true);
    }
  }, [isExpanded, onExpandedVisualChange]);

  return (
    <tr aria-hidden={!isExpanded}>
      <td className="p-0" colSpan={colSpan} ref={cellRef}>
        <motion.div
          animate={isExpanded ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
          className={allowOverflow ? 'overflow-visible' : 'overflow-hidden'}
          initial={false}
          onAnimationComplete={() => {
            measureColumnWidths();
            if (isExpanded) {
              setAllowOverflow(true);
            } else {
              onExpandedVisualChange?.(false);
            }
          }}
          transition={EXPANDED_TABLE_ROWS_TRANSITION}
        >
          {syncColumns ? (
            <table className="w-full table-fixed">
              {columnWidths.length > 0 && (
                <colgroup>
                  {columnWidths.map((width, index) => (
                    <col key={index} style={{ width }} />
                  ))}
                </colgroup>
              )}
              {contentKey === undefined ? (
                <tbody>{children}</tbody>
              ) : (
                <motion.tbody
                  animate={{ opacity: 1 }}
                  initial={{ opacity: 0 }}
                  key={contentKey}
                  transition={CONTENT_FADE_TRANSITION}
                >
                  {children}
                </motion.tbody>
              )}
            </table>
          ) : contentKey === undefined ? (
            children
          ) : (
            <motion.div
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              key={contentKey}
              transition={CONTENT_FADE_TRANSITION}
            >
              {children}
            </motion.div>
          )}
        </motion.div>
      </td>
    </tr>
  );
};
